import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/auth.store';

interface NavItem {
  name: string;
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children?: { name: string; to: string; icon?: React.ComponentType<React.SVGProps<SVGSVGElement>> }[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', to: '/', icon: HomeIcon },
  { name: 'Clients', to: '/clients', icon: BuildingOfficeIcon },
  { name: 'Contacts', to: '/contacts', icon: UserGroupIcon },
  { name: 'Projects', to: '/projects', icon: FolderIcon },
  {
    name: 'Surveys',
    to: '/surveys',
    icon: ClipboardDocumentListIcon,
    children: [
      { name: 'Templates', to: '/surveys/templates', icon: DocumentTextIcon },
      { name: 'Dispatches', to: '/surveys/dispatches', icon: PaperAirplaneIcon },
    ],
  },
  { name: 'Reports', to: '/reports', icon: ChartBarIcon },
  {
    name: 'Settings', to: '/settings', icon: Cog6ToothIcon,
    children: [
      { name: 'General', to: '/settings' },
      { name: 'Users', to: '/settings/users' },
      { name: 'Audit Log', to: '/settings/audit-log' },
    ],
  },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const linkClasses = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-indigo-700 text-white'
        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
    }`;

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      const isExpanded = expandedItems[item.name] || false;
      return (
        <div key={item.name}>
          <button
            onClick={() => setExpandedItems(prev => ({ ...prev, [item.name]: !prev[item.name] }))}
            className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
          >
            <span className="flex items-center gap-3">
              <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {item.name}
            </span>
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children.map((child) => (
                <NavLink
                  key={child.name}
                  to={child.to}
                  className={linkClasses}
                  onClick={() => setSidebarOpen(false)}
                >
                  {child.name}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.name}
        to={item.to}
        end={item.to === '/'}
        className={linkClasses}
        onClick={() => setSidebarOpen(false)}
      >
        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {item.name}
      </NavLink>
    );
  };

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Logo / Brand */}
      <div className="flex h-16 items-center px-4 border-b border-gray-700">
        <h1 className="text-xl font-bold text-white tracking-tight">CSAT Survey</h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navigation.map(renderNavItem)}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-600/75"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />

          {/* Sidebar panel */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-gray-800">
            <div className="absolute right-0 top-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col bg-gray-800">
          {sidebarContent}
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:px-6">
          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* User info and logout */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex sm:flex-col sm:items-end">
              <p className="text-sm font-semibold text-gray-900">
                {user?.full_name ?? user?.name ?? 'User'}
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {user?.role ?? ''}
              </p>
            </div>
            <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {(user?.full_name ?? user?.name)
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) ?? 'U'}
              </span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              title="Sign out"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
